import React, { useState } from 'react';
import styles from '../app.module.css'

const Responses = ({
  responses = {},
  existingHeaders = {},
  addResponse,
  editResponse,
  removeResponse,
  schemas = [],
  examples = [],
}) => {
  const [currentResponse, setCurrentResponse] = useState(null);
  const [responseData, setResponseData] = useState({
    name: '',
    description: '',
    contentType: '',
    schema: '',
    example: '',
    selectedHeaders: [],
  });

  const [popupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const showPopupModal = (message) => {
    setPopupMessage(message);
    setShowPopup(true);
  }

  const handleInputChange = (field, value) => {
    setResponseData((prev) => ({ ...prev, [field]: value }));
  };

  const handleHeaderChange = (headerName) => {
    setResponseData((prev) => {
      const updatedHeaders = prev.selectedHeaders.includes(headerName)
        ? prev.selectedHeaders.filter((h) => h !== headerName)
        : [...prev.selectedHeaders, headerName];
      return { ...prev, selectedHeaders: updatedHeaders };
    });
  };

  const handleAddResponse = () => {
    setResponseData({ name: '', description: '', contentType: '', schema: '', example: '', selectedHeaders: [] });
    setCurrentResponse(null);
    setPopupVisible(true);
  };

  const handleEditResponse = (name) => {
    const response = responses[name] || {};
    setResponseData({
      name,
      description: response.description || '',
      contentType: Object.keys(response.content || {})[0] || '',
      schema: response.content?.[Object.keys(response.content || {})[0]]?.schema?.$ref || '',
      example: response.content?.[Object.keys(response.content || {})[0]]?.examples
        ? Object.keys(response.content[Object.keys(response.content)[0]].examples)[0]
        : '',
      selectedHeaders: response.headers ? Object.keys(response.headers) : [],
    });
    setCurrentResponse(name);
    setPopupVisible(true);
  };

  const handleSaveResponse = () => {
    const { name, description, contentType, schema, example, selectedHeaders } = responseData;

    if (!currentResponse && responses[name]) {
      showPopupModal(`A response with the name "${name}" already exists. Please use a different name.`);
      return;
    }

    if (!name || !contentType || !schema) {
      showPopupModal('All fields are required except description and examples.');
      return;
    }

    const content = {
      [contentType]: {
        schema: {
          $ref: schema,
        },
      },
    };

    if (example) {
      content[contentType].examples = {
        [example]: {
          $ref: `#/components/examples/${example}`,
        },
      };
    }

    const headersToSave =
      selectedHeaders.length > 0
        ? selectedHeaders.reduce((acc, headerName) => {
          if (existingHeaders[headerName]) {
            acc[headerName] = existingHeaders[headerName];
          }
          return acc;
        }, {})
        : undefined;

    const responseToSave = {
      description,
      content,
      ...(headersToSave ? { headers: headersToSave } : {}),
    };

    if (currentResponse) {
      editResponse(currentResponse, responseToSave);
    } else {
      addResponse(name, responseToSave);
    }

    handleClosePopup();
  };

  const handleDeleteResponse = (name) => {
    if (window.confirm(`Are you sure you want to delete the response "${name}"?`)) {
      removeResponse(name);
    }
  };

  const handleClosePopup = () => {
    setPopupVisible(false);
    setResponseData({ name: '', description: '', contentType: '', schema: '', example: '', selectedHeaders: [] });
    setCurrentResponse(null);
  };

  return (
    <div className="responses-container">
      <h3 className={styles.centeredHeading}>Responses</h3>

      {Object.keys(responses).length > 0 && (
        <div>
          {Object.entries(responses).map(([name, response]) => (
            <div
              key={name}
              className={styles.mainContainer}
            >
              <p>
                <strong>Name:<span className={styles.requiredField}>*</span></strong> {name}
              </p>
              <p>
                <strong>Description:</strong> {response.description}
              </p>
              <p>
                <strong>Content Type: <span className={styles.requiredField}>*</span></strong> {Object.keys(response.content || {})[0]}
              </p>
              <p>
                <strong>Schema:<span className={styles.requiredField}>*</span></strong>{' '}
                {response.content &&
                  Object.values(response.content).length &&
                  response.content[Object.keys(response.content)[0]].schema
                  ? response.content[Object.keys(response.content)[0]].schema.$ref.replace(
                    '#/components/schemas/',
                    ''
                  )
                  : 'None'}
              </p>
              <p>
                <strong>Example:</strong>{' '}
                {response.content?.[Object.keys(response.content || {})[0]]?.examples
                  ? Object.keys(response.content[Object.keys(response.content || {})[0]].examples)[0]
                  : 'None'}
              </p>
              <p><strong>Headers:</strong> {Object.keys(response.headers || {}).join(', ') || 'None'}</p>
              <div className={styles.actionButtonsContainer}              >
                <button
                  onClick={() => handleEditResponse(name)}
                  className={styles.editButton}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteResponse(name)}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={handleAddResponse} className={styles.addNewButton}>+ Add Response</button>

      {popupVisible && (
        <div className={styles.popupContainer}>
          <h3>{currentResponse ? 'Edit Response' : 'Add Response'}</h3>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Name: <span className={styles.requiredField}>*</span></label>
            <input
              type="text"
              value={responseData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={styles.textBox}
              disabled={!!currentResponse}
            />
          </div>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Description:</label>
            <textarea
              value={responseData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className={styles.textBox}
            />
          </div>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Content Type: <span className={styles.requiredField}>*</span></label>
            <select
              value={responseData.contentType}
              onChange={(e) => handleInputChange('contentType', e.target.value)}
              className={styles.dropdown}
            >
              <option value="">Select Content Type</option>
              <option value="application/json">application/json</option>
              <option value="application/xml">application/xml</option>
            </select>
          </div>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Schema: <span className={styles.requiredField}>*</span></label>
            <select
              value={responseData.schema}
              onChange={(e) => handleInputChange('schema', e.target.value)}
              className={styles.dropdown}
            >
              <option value="">Select Schema</option>
              {schemas?.map((schema) => (
                <option key={schema} value={`#/components/schemas/${schema}`}>
                  {schema}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.style3}>
            <label className={styles.labelStyle}>Example:</label>
            <select
              value={responseData.example}
              onChange={(e) => handleInputChange('example', e.target.value)}
              className={styles.dropdown}
            >
              <option value="">Select Example</option>
              {examples?.map((example) => (
                <option key={example} value={example}>
                  {example}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Headers:</label>
            {existingHeaders && Object.keys(existingHeaders).length > 0 ? (
              Object.keys(existingHeaders).map((headerName) => (
                <div key={headerName}>
                  <input
                    type="checkbox"
                    checked={responseData.selectedHeaders.includes(headerName)}
                    onChange={() => handleHeaderChange(headerName)}
                  />
                  <label>{headerName}</label>
                </div>
              ))
            ) : (
              <p className={styles.infoText}>No headers available.</p>
            )}
          </div>

          <div className={styles.sNcButtonContainer}>
            <button
              onClick={handleSaveResponse}
              className={styles.saveButton}
            >
              Save
            </button>
            <button
              onClick={handleClosePopup}
              className={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {popupVisible && (
        <div
          className={styles.overlay}
          onClick={handleClosePopup}
        ></div>
      )}

      {showPopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            <h3>Error</h3>
            <p>{popupMessage}</p>
            <button onClick={() => setShowPopup(false)} className="popup-close-button">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Responses;
